import React, { createContext, useContext, useState, useCallback } from 'react';

import { useToast } from './toast';

interface ProductsType {
  id: number;
  unidade_medida_id: number;
  descricao: string;
  codigo?: string;
  gtin?: string;
  tipo: number;
  preco: number;
  observacao: string;
  imagem: string;
  estoque_minimo: number;
  estoque_atual: number;
  grupo_id: number;
}

interface ITurnoType {
  inicio: number;
  fim: number;
}

interface DisponibilidadeType {
  dias: {
    domingo: boolean;
    segunda: boolean;
    terca: boolean;
    quarta: boolean;
    quinta: boolean;
    sexta: boolean;
    sabado: boolean;
  };
  turnos: ITurnoType[];
}

interface QtdTurnos {
  index: number;
}

interface TurnoType {
  inicio: number;
  fim: number;
}

interface EditProdutoContextData {
  produto: ProductsType;
  disponibilidade: DisponibilidadeType;
  cloneDrawerOpen: boolean;
  qtdTurnos: QtdTurnos[];
  turnos: TurnoType[];
  sempreDisponivel: boolean;

  handleSetProduto(data: ProductsType): void;
  handleSetDisponibilidade(data: DisponibilidadeType): void;
  handleSetCloneDrawerOpen(value: boolean): void;
  handleSetQtdTurno(operacao: string, item_index?: number): void;
  handleSetSempreDisponivel(value: boolean): void;
  handleSetTurnos(data: TurnoType[]): void;
  handleSetInitialQtdTurnos(data: QtdTurnos[]): void;
  cleanData(): void;
}

const CloneProdutoContext = createContext<EditProdutoContextData>(
  {} as EditProdutoContextData,
);

const CloneProdutoProvider: React.FC = ({ children }) => {
  const [produto, setProduto] = useState<ProductsType>(() => {
    return {} as ProductsType;
  });
  const [disponibilidade, setDisponibilidade] = useState<DisponibilidadeType>(
    () => {
      return {} as DisponibilidadeType;
    },
  );
  const [cloneDrawerOpen, setCloneDrawerOpen] = useState(false);
  const [sempreDisponivel, setSempreDisponivel] = useState(true);
  const [qtdTurnos, setQtdTurnos] = useState<QtdTurnos[]>([]);
  const [turnos, setTurnos] = useState<TurnoType[]>([]);

  const handleSetProduto = useCallback((data: ProductsType) => {
    setProduto({ ...data });
  }, []);

  const handleSetDisponibilidade = useCallback((data: DisponibilidadeType) => {
    setDisponibilidade({ ...data });
  }, []);

  const handleSetCloneDrawerOpen = useCallback((value: boolean) => {
    setCloneDrawerOpen(value);
  }, []);

  const handleSetSempreDisponivel = useCallback((value: boolean) => {
    setSempreDisponivel(value);
  }, []);

  const handleSetTurnos = useCallback((data: TurnoType[]) => {
    setTurnos(data);
  }, []);

  const cleanData = useCallback(() => {
    setProduto({} as ProductsType);
    setDisponibilidade({} as DisponibilidadeType);
  }, []);

  const handleSetInitialQtdTurnos = useCallback((data: QtdTurnos[]) => {
    setQtdTurnos([...data]);
  }, []);

  const handleSetQtdTurno = useCallback(
    (operacao: string, item_index?: number) => {
      const qtdAtual = [...qtdTurnos];
      const turnosAtuais = [...turnos];

      if (operacao === 'add') {
        if (qtdTurnos.length == 0) {
          setQtdTurnos([{ index: 0 }]);
          setTurnos([{ inicio: 0, fim: 0 }]);
          return;
        }

        qtdAtual.push({ index: qtdAtual[qtdTurnos.length - 1].index + 1 });
        turnosAtuais.push({ inicio: 0, fim: 0 });

        setQtdTurnos([...qtdAtual]);
        setTurnos([...turnosAtuais]);
      } else if (operacao === 'remove') {
        const qtdTurnosStoreds: QtdTurnos[] = qtdTurnos;
        const turnosStored = [...turnos];

        if (item_index !== undefined) {
          turnosStored.splice(item_index, 1);
        }
        qtdTurnosStoreds.pop();

        setQtdTurnos([...qtdTurnosStoreds]);
        setTurnos([...turnosStored]);
      }
    },
    [qtdTurnos, turnos],
  );

  return (
    <CloneProdutoContext.Provider
      value={{
        produto,
        disponibilidade,
        cloneDrawerOpen,
        qtdTurnos,
        turnos,
        sempreDisponivel,
        handleSetProduto,
        handleSetDisponibilidade,
        handleSetCloneDrawerOpen,
        handleSetQtdTurno,
        handleSetSempreDisponivel,
        handleSetTurnos,
        handleSetInitialQtdTurnos,
        cleanData,
      }}
    >
      {children}
    </CloneProdutoContext.Provider>
  );
};

function useCloneProduto(): EditProdutoContextData {
  const context = useContext(CloneProdutoContext);

  if (!context)
    throw new Error(
      'useCloneProduto deve ser utilizado junto do CloneProdutoProvider',
    );

  return context;
}

export { CloneProdutoProvider, useCloneProduto };
